import type { Coord } from "@dailywars/core/engine";
import { useEffect, useState } from "react";

import { useGameCanvasOverlayLayout } from "#/components/game/gameCanvas/overlayLayoutContext";

export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function useTileScreenRect({
  coord,
  boardWidth,
  boardHeight,
}: {
  coord: Coord | null;
  boardWidth: number;
  boardHeight: number;
}): ScreenRect | null {
  const { canvasRef } = useGameCanvasOverlayLayout();
  const [rect, setRect] = useState<ScreenRect | null>(null);

  useEffect(() => {
    if (!coord) return;
    let raf = 0;
    let prevKey: string | null = null;
    const measure = () => {
      const canvas = canvasRef.current;
      const box = canvas?.getBoundingClientRect();
      if (box && box.width > 0 && box.height > 0) {
        const tileW = box.width / boardWidth;
        const tileH = box.height / boardHeight;
        const next: ScreenRect = {
          left: box.left + coord.x * tileW,
          top: box.top + coord.y * tileH,
          width: tileW,
          height: tileH,
        };
        const key = `${next.left}|${next.top}|${next.width}|${next.height}`;
        if (key !== prevKey) {
          prevKey = key;
          setRect(next);
        }
      }
      raf = requestAnimationFrame(measure);
    };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [canvasRef, coord, boardWidth, boardHeight]);

  return coord ? rect : null;
}
